import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useFittedVisibleTagCount } from "@/features/invoices/hooks/useFittedVisibleTagCount";
import { cn } from "@/utils/cn";

export interface CustomerSelectOption {
  value: string;
  title: string;
  subtitle?: string;
}

interface CustomerMultiSelectProps {
  label: string;
  options: CustomerSelectOption[];
  /** Empty = all customers */
  selected: string[];
  onChange: (selected: string[]) => void;
  allLabel?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Fixed tag count before +N; omit to auto-fit one row */
  maxVisibleTags?: number;
}

const VIRTUAL_THRESHOLD = 100;
const ROW_HEIGHT = 44;
/** ~3 rows — selected block scrolls beyond this */
const SELECTED_LIST_MAX_HEIGHT = 132;
/** Unselected list always keeps at least this much visible space */
const AVAILABLE_SECTION_MIN_HEIGHT = 168;

function matchesQuery(opt: CustomerSelectOption, q: string): boolean {
  if (!q) return true;
  const hay = `${opt.title} ${opt.subtitle ?? ""} ${opt.value}`.toLowerCase();
  return hay.includes(q);
}

function VirtualOptionList({
  items,
  rowHeight,
  maxHeight,
  activeIndex,
  activeGlobalOffset,
  onToggle,
  onRowRef,
}: {
  items: CustomerSelectOption[];
  rowHeight: number;
  maxHeight: number;
  activeIndex: number;
  activeGlobalOffset: number;
  onToggle: (value: string) => void;
  onRowRef: (index: number, el: HTMLButtonElement | null) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const totalHeight = items.length * rowHeight;
  const visibleCount = Math.ceil(maxHeight / rowHeight) + 2;
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 1);
  const end = Math.min(items.length, start + visibleCount);
  const slice = items.slice(start, end);
  const paddingTop = start * rowHeight;
  const paddingBottom = Math.max(0, totalHeight - end * rowHeight);

  useEffect(() => {
    if (activeIndex < activeGlobalOffset || activeIndex >= activeGlobalOffset + items.length) {
      return;
    }
    const local = activeIndex - activeGlobalOffset;
    const el = scrollRef.current;
    if (!el) return;
    const rowTop = local * rowHeight;
    const rowBottom = rowTop + rowHeight;
    if (rowTop < el.scrollTop) {
      el.scrollTop = rowTop;
    } else if (rowBottom > el.scrollTop + maxHeight) {
      el.scrollTop = rowBottom - maxHeight;
    }
  }, [activeIndex, activeGlobalOffset, items.length, rowHeight, maxHeight]);

  return (
    <div
      ref={scrollRef}
      className="overflow-y-auto"
      style={{ maxHeight }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ paddingTop, paddingBottom }}>
        {slice.map((opt, i) => {
          const localIndex = start + i;
          const globalIndex = activeGlobalOffset + localIndex;
          return (
            <OptionRow
              key={opt.value}
              option={opt}
              checked={false}
              active={activeIndex === globalIndex}
              onToggle={() => onToggle(opt.value)}
              buttonRef={(el) => onRowRef(globalIndex, el)}
            />
          );
        })}
      </div>
    </div>
  );
}

function OptionRow({
  option,
  checked,
  active,
  onToggle,
  buttonRef,
}: {
  option: CustomerSelectOption;
  checked: boolean;
  active: boolean;
  onToggle: () => void;
  buttonRef?: (el: HTMLButtonElement | null) => void;
}) {
  return (
  <button
    type="button"
    ref={buttonRef}
    role="option"
    aria-selected={checked}
    className={cn(
      "flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left text-sm transition",
      active && "bg-[#e6fffb] ring-1 ring-[var(--accent)] ring-inset",
      !active && "hover:bg-[rgba(0,0,0,0.04)]",
    )}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
  >
    <span
      className={cn(
        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold",
        checked
          ? "border-[var(--accent)] bg-[var(--accent)] text-white"
          : "border-[#d9d9d9] bg-white text-transparent",
      )}
      aria-hidden
    >
      ✓
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-medium leading-snug">{option.title}</span>
      {option.subtitle ? (
        <span className="block truncate text-xs text-[var(--muted)]">
          {option.subtitle}
        </span>
      ) : null}
    </span>
  </button>
  );
}

export function CustomerMultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel = "All customers",
  searchPlaceholder = "Search by email or name…",
  disabled,
  loading,
  maxVisibleTags,
}: CustomerMultiSelectProps) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const isAll = selected.length === 0;
  const query = search.trim().toLowerCase();

  const optionMap = useMemo(() => {
    const m = new Map<string, CustomerSelectOption>();
    for (const o of options) m.set(o.value, o);
    return m;
  }, [options]);

  const resolve = useCallback(
    (value: string): CustomerSelectOption =>
      optionMap.get(value) ?? { value, title: value, subtitle: value },
    [optionMap],
  );

  const selectedOptions = useMemo(
    () => selected.map((id) => resolve(id)),
    [selected, resolve],
  );

  const { selectedFiltered, availableFiltered } = useMemo(() => {
    const selectedSet = new Set(selected);
    const sel: CustomerSelectOption[] = [];
    const avail: CustomerSelectOption[] = [];

    for (const opt of options) {
      const inSelected = selectedSet.has(opt.value);
      const matches = matchesQuery(opt, query);
      if (inSelected && matches) sel.push(opt);
      if (!inSelected && matches) avail.push(opt);
    }

    for (const id of selected) {
      if (!optionMap.has(id)) {
        const ghost = resolve(id);
        if (matchesQuery(ghost, query)) sel.push(ghost);
      }
    }

    return { selectedFiltered: sel, availableFiltered: avail };
  }, [options, selected, query, optionMap, resolve]);

  const flatRows = useMemo(
    () => [...selectedFiltered, ...availableFiltered],
    [selectedFiltered, availableFiltered],
  );

  const availableGlobalOffset = selectedFiltered.length;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setActiveIndex(-1);
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const toggle = (value: string) => {
    const has = selected.includes(value);
    if (has) {
      onChange(selected.filter((v) => v !== value));
      return;
    }
    onChange(isAll ? [value] : [...selected, value]);
  };

  const clearAll = () => {
    onChange([]);
  };

  const removeTag = (value: string) => {
    const next = selected.filter((v) => v !== value);
    onChange(next);
  };

  const setRowRef = (index: number, el: HTMLButtonElement | null) => {
    if (el) rowRefs.current.set(index, el);
    else rowRefs.current.delete(index);
  };

  useEffect(() => {
    if (activeIndex < 0) return;
    rowRefs.current.get(activeIndex)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const onDropdownKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }

    const rowCount = flatRows.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => {
        if (i < 0) return rowCount > 0 ? 0 : -1;
        return Math.min(i + 1, rowCount - 1);
      });
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => {
        if (i <= 0) {
          searchRef.current?.focus();
          return -1;
        }
        return i - 1;
      });
      return;
    }

    if (e.key === "Enter" && activeIndex >= 0 && activeIndex < rowCount) {
      e.preventDefault();
      toggle(flatRows[activeIndex].value);
    }
  };

  const triggerTags = isAll
    ? [{ key: "__all__", label: allLabel, value: null as string | null }]
    : selectedOptions.map((o) => ({
        key: o.value,
        label: o.title,
        value: o.value,
      }));

  const fittedCount = useFittedVisibleTagCount(
    triggerTags.length,
    tagsContainerRef,
    measureRef,
    maxVisibleTags,
  );
  const visibleTags = triggerTags.slice(0, fittedCount);
  const hiddenCount = triggerTags.length - visibleTags.length;

  const useVirtual = availableFiltered.length > VIRTUAL_THRESHOLD;

  return (
    <div
      ref={rootRef}
      className="customer-multi-select relative min-w-0 w-full"
      data-customer-picker-open={open ? "true" : undefined}
    >
      <label
        htmlFor={id}
        className="mb-1 block text-[0.72rem] font-semibold text-[var(--muted)]"
      >
        {label}
      </label>

      <button
        id={id}
        type="button"
        disabled={disabled || loading}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn("filter-select-trigger", open && "is-open")}
        onClick={() => setOpen((o) => !o)}
      >
        <span ref={tagsContainerRef} className="filter-select-trigger__tags">
          {visibleTags.map((tag) => (
            <span
              key={tag.key}
              className={cn(
                "filter-select-tag",
                tag.key === "__all__"
                  ? "filter-select-tag--all"
                  : "filter-select-tag--default",
              )}
            >
              <span className="filter-select-tag__label">{tag.label}</span>
              {tag.value ? (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${tag.label}`}
                  className="shrink-0 cursor-pointer text-[rgba(0,0,0,0.45)] hover:text-[var(--text)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag.value!);
                  }}
                >
                  ×
                </span>
              ) : null}
            </span>
          ))}
          {hiddenCount > 0 ? (
            <span className="filter-select-tag filter-select-tag--muted shrink-0">
              +{hiddenCount}
            </span>
          ) : null}
          <span
            ref={measureRef}
            className="filter-select-trigger__measure"
            aria-hidden
          >
            {triggerTags.map((tag) => (
              <span
                key={tag.key}
                data-measure-tag
                className={cn(
                  "filter-select-tag",
                  tag.key === "__all__"
                    ? "filter-select-tag--all"
                    : "filter-select-tag--default",
                )}
              >
                <span className="filter-select-tag__label">{tag.label}</span>
                {tag.value ? (
                  <span className="shrink-0" aria-hidden>
                    ×
                  </span>
                ) : null}
              </span>
            ))}
            <span
              data-measure-overflow
              className="filter-select-tag filter-select-tag--muted shrink-0"
            >
              +9
            </span>
          </span>
        </span>
        <span
          className={cn(
            "pointer-events-none shrink-0 px-2 text-[rgba(0,0,0,0.45)] transition",
            open && "rotate-180 text-[var(--accent)]",
          )}
          aria-hidden
        >
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          aria-label={label}
          className="customer-multi-select-dropdown filter-select-dropdown absolute left-0 top-[calc(100%+4px)] z-[120] flex max-h-[min(24rem,70vh)] flex-col overflow-hidden rounded-lg border border-[var(--border-soft)] bg-white shadow-[0_6px_16px_rgba(0,0,0,0.08)]"
          onKeyDown={onDropdownKeyDown}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-[2] border-b border-[var(--border-soft)] bg-white px-2 pb-2 pt-2">
            <div className="relative">
              <span
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]"
                aria-hidden
              >
                ⌕
              </span>
              <input
                ref={searchRef}
                type="search"
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-[#d9d9d9] py-1.5 pl-8 pr-3 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[rgba(15,118,110,0.12)]"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setActiveIndex(-1);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown" && flatRows.length > 0) {
                    e.preventDefault();
                    setActiveIndex(0);
                  }
                }}
              />
            </div>
          </div>

          <div className="customer-multi-select-body flex min-h-0 flex-1 flex-col">
            {!isAll && selectedFiltered.length > 0 ? (
              <section className="customer-multi-select-selected shrink-0 border-b-2 border-[#d9d9d9] bg-[#f0f7f6] shadow-[inset_0_-2px_0_rgba(15,118,110,0.08)]">
                <div className="flex items-center justify-between gap-2 px-3 pb-1.5 pt-2">
                  <span className="text-xs font-bold uppercase tracking-wide text-[var(--accent)]">
                    Selected ({selected.length})
                  </span>
                  <button
                    type="button"
                    className="text-xs font-semibold text-[var(--accent)] hover:underline"
                    onClick={clearAll}
                  >
                    Clear all
                  </button>
                </div>
                <div
                  className="space-y-0.5 overflow-y-auto px-2 pb-2"
                  style={{ maxHeight: SELECTED_LIST_MAX_HEIGHT }}
                >
                  {selectedFiltered.map((opt, i) => (
                    <OptionRow
                      key={`sel-${opt.value}`}
                      option={opt}
                      checked
                      active={activeIndex === i}
                      onToggle={() => toggle(opt.value)}
                      buttonRef={(el) => setRowRef(i, el)}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            <section
              className="customer-multi-select-available flex min-h-0 flex-1 flex-col bg-white"
              style={{ minHeight: AVAILABLE_SECTION_MIN_HEIGHT }}
            >
              {!isAll && selectedFiltered.length > 0 ? (
                <div className="shrink-0 border-y border-[#d9d9d9] bg-[var(--surface-2)] px-3 py-1.5">
                  <p className="text-center text-[0.65rem] font-bold uppercase tracking-wider text-[var(--muted)]">
                    Add more customers
                  </p>
                </div>
              ) : null}
              <div className="flex min-h-0 flex-1 flex-col px-2 pb-2 pt-1.5">
                <p className="mb-1.5 shrink-0 px-1 text-xs font-bold uppercase tracking-wide text-[var(--muted)]">
                  {query
                    ? `Matching (${availableFiltered.length})`
                    : `All options (${availableFiltered.length} remaining)`}
                </p>
                {availableFiltered.length === 0 ? (
                  <p className="px-1 py-3 text-center text-xs text-[var(--muted)]">
                    {query ? "No customers match your search." : "No more customers."}
                  </p>
                ) : useVirtual ? (
                  <VirtualOptionList
                    items={availableFiltered}
                    rowHeight={ROW_HEIGHT}
                    maxHeight={AVAILABLE_SECTION_MIN_HEIGHT - 40}
                    activeIndex={activeIndex}
                    activeGlobalOffset={availableGlobalOffset}
                    onToggle={toggle}
                    onRowRef={setRowRef}
                  />
                ) : (
                  <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                    {availableFiltered.map((opt, i) => {
                      const gi = availableGlobalOffset + i;
                      return (
                        <OptionRow
                          key={opt.value}
                          option={opt}
                          checked={false}
                          active={activeIndex === gi}
                          onToggle={() => toggle(opt.value)}
                          buttonRef={(el) => setRowRef(gi, el)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>

          {options.length >= 100 ? (
            <p className="border-t border-[var(--border-soft)] px-3 py-1.5 text-[10px] text-[var(--muted)]">
              Showing first {options.length} customers from Stripe. Use search to narrow.
            </p>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <p className="mt-1 text-xs text-[var(--muted)]">Loading customers…</p>
      ) : null}
    </div>
  );
}
